import { SociiMessage, SociiChannel } from '../../types/nostr';
import { openDB, DBSchema, IDBPDatabase } from 'idb';

interface SociiDB extends DBSchema {
  messages: {
    key: string;
    value: SociiMessage;
    indexes: {
      'by-channel': string;
      'by-timestamp': number;
      'by-author': string;
    };
  };
  channels: {
    key: string;
    value: SociiChannel;
    indexes: {
      'by-type': string;
    };
  };
  identity: {
    key: string;
    value: {
      privateKeyHex: string;
      publicKeyHex: string;
      npub: string;
      createdAt: Date;
    };
  };
}

export class IndexedDBStorage {
  private db: IDBPDatabase<SociiDB> | null = null;

  async initialize(): Promise<void> {
    this.db = await openDB<SociiDB>('socii-db', 1, {
      upgrade(db) {
        // Messages store
        const messageStore = db.createObjectStore('messages', {
          keyPath: 'id'
        });
        messageStore.createIndex('by-channel', 'channelId');
        messageStore.createIndex('by-timestamp', 'created_at');
        messageStore.createIndex('by-author', 'pubkey');

        // Channels store
        const channelStore = db.createObjectStore('channels', {
          keyPath: 'id'
        });
        channelStore.createIndex('by-type', 'type');

        // Identity store
        db.createObjectStore('identity', {
          keyPath: 'id'
        });
      }
    });
  }

  // Message operations
  async saveMessage(message: SociiMessage): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');
    await this.db.put('messages', message);
  }

  async getMessages(channelId: string, limit: number = 50): Promise<SociiMessage[]> {
    if (!this.db) throw new Error('Database not initialized');

    const index = this.db.transaction('messages').store.index('by-channel');
    const messages = await index.getAll(channelId);

    // Sort by timestamp (newest first) and limit
    return messages
      .sort((a, b) => b.created_at - a.created_at)
      .slice(0, limit)
      .reverse(); // Reverse to show oldest first in UI
  }

  async getMessage(messageId: string): Promise<SociiMessage | undefined> {
    if (!this.db) throw new Error('Database not initialized');
    return await this.db.get('messages', messageId);
  }

  async deleteMessage(messageId: string): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');
    await this.db.delete('messages', messageId);
  }

  async clearMessages(channelId?: string): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');

    if (channelId) {
      const index = this.db.transaction('messages').store.index('by-channel');
      const keys = await index.getAllKeys(channelId);
      const tx = this.db.transaction('messages', 'readwrite');
      await Promise.all(keys.map(key => tx.store.delete(key)));
      await tx.done;
    } else {
      await this.db.clear('messages');
    }
  }

  // Channel operations
  async saveChannel(channel: SociiChannel): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');
    await this.db.put('channels', channel);
  }

  async getChannels(): Promise<SociiChannel[]> {
    if (!this.db) throw new Error('Database not initialized');
    return await this.db.getAll('channels');
  }

  async getChannel(channelId: string): Promise<SociiChannel | undefined> {
    if (!this.db) throw new Error('Database not initialized');
    return await this.db.get('channels', channelId);
  }

  async deleteChannel(channelId: string): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');
    await this.db.delete('channels', channelId);
  }

  // Identity operations
  async saveIdentity(identity: {
    privateKeyHex: string;
    publicKeyHex: string;
    npub: string;
    createdAt: Date;
  }): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');
    await this.db.put('identity', identity);
  }

  async getIdentity(): Promise<{
    privateKeyHex: string;
    publicKeyHex: string;
    npub: string;
    createdAt: Date;
  } | undefined> {
    if (!this.db) throw new Error('Database not initialized');
    return await this.db.get('identity', 'current');
  }

  async clearIdentity(): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');
    await this.db.delete('identity', 'current');
  }

  // Utility methods
  async getStats(): Promise<{
    totalMessages: number;
    channelsCount: number;
    oldestMessage?: Date;
    newestMessage?: Date;
  }> {
    if (!this.db) throw new Error('Database not initialized');

    const messages = await this.db.getAll('messages');
    const channels = await this.db.getAll('channels');

    const timestamps = messages.map(m => m.created_at);
    const oldestTimestamp = timestamps.length > 0 ? Math.min(...timestamps) : undefined;
    const newestTimestamp = timestamps.length > 0 ? Math.max(...timestamps) : undefined;

    return {
      totalMessages: messages.length,
      channelsCount: channels.length,
      oldestMessage: oldestTimestamp ? new Date(oldestTimestamp * 1000) : undefined,
      newestMessage: newestTimestamp ? new Date(newestTimestamp * 1000) : undefined
    };
  }

  // Cleanup old messages (keep last N messages per channel)
  async cleanupOldMessages(channelId: string, keepCount: number = 1000): Promise<number> {
    if (!this.db) throw new Error('Database not initialized');

    const messages = await this.getMessages(channelId, keepCount * 2);
    if (messages.length <= keepCount) return 0;

    const messagesToDelete = messages.slice(0, messages.length - keepCount);
    const deletePromises = messagesToDelete.map(msg =>
      this.db!.delete('messages', msg.id!)
    );

    await Promise.all(deletePromises);
    return messagesToDelete.length;
  }

  // Search messages
  async searchMessages(query: string, channelId?: string): Promise<SociiMessage[]> {
    if (!this.db) throw new Error('Database not initialized');

    let messages: SociiMessage[];

    if (channelId) {
      messages = await this.getMessages(channelId, 1000); // Get more for search
    } else {
      messages = await this.db.getAll('messages');
    }

    const searchTerm = query.toLowerCase();
    return messages.filter(msg =>
      msg.content.toLowerCase().includes(searchTerm)
    );
  }

  // Export/Import data
  async exportData(): Promise<string> {
    if (!this.db) throw new Error('Database not initialized');

    const [messages, channels, identity] = await Promise.all([
      this.db.getAll('messages'),
      this.db.getAll('channels'),
      this.db.get('identity', 'current')
    ]);

    const data = {
      messages,
      channels,
      identity,
      exportDate: new Date().toISOString()
    };

    return JSON.stringify(data, null, 2);
  }

  async importData(jsonData: string): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');

    try {
      const data = JSON.parse(jsonData);

      // Clear existing data
      await Promise.all([
        this.db.clear('messages'),
        this.db.clear('channels'),
        this.db.clear('identity')
      ]);

      // Import new data
      if (data.messages) {
        await Promise.all(data.messages.map((msg: SociiMessage) =>
          this.db!.put('messages', msg)
        ));
      }

      if (data.channels) {
        await Promise.all(data.channels.map((channel: SociiChannel) =>
          this.db!.put('channels', channel)
        ));
      }

      if (data.identity) {
        await this.db.put('identity', { id: 'current', ...data.identity });
      }

      console.log('Data import completed successfully');
    } catch (error) {
      console.error('Failed to import data:', error);
      throw error;
    }
  }
}
